import { Workbox } from 'workbox-window';
import { env } from '@/shared/config/env';
import { getStoreRef } from '@/shared/store';
import { setInstallAvailable, setUpdateAvailable } from '@/shared/store/slices/uiSlice';
import { captureInstallPrompt } from './installPrompt';
import { createPwaController, type PwaController } from './pwaController';

const SW_URL = '/sw.js';

let controller: PwaController | null = null;

/** El SW solo se registra en producción (en desarrollo estorba al HMR) y nunca en modo demostración. */
export const shouldRegisterServiceWorker = (): boolean =>
  env.isProd && !env.demoMode && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

function createController(): PwaController {
  const { dispatch } = getStoreRef();
  return createPwaController({
    createWorkbox: () => {
      const wb = new Workbox(SW_URL, { scope: '/' });
      return {
        addEventListener: (type, listener) => wb.addEventListener(type, listener),
        register: () => wb.register(),
        messageSkipWaiting: () => wb.messageSkipWaiting(),
      };
    },
    setUpdateAvailable: (value) => dispatch(setUpdateAvailable(value)),
    reload: () => window.location.reload(),
    isOnline: () => navigator.onLine,
    setInterval: (callback, ms) => window.setInterval(callback, ms),
  });
}

/**
 * Arranca la parte PWA: captura `beforeinstallprompt` y registra el service worker. Se llama una vez
 * desde `main.tsx` después de montar la app. Marca `ui.updateAvailable` cuando hay una versión nueva.
 */
export function initPwa(): void {
  if (controller || !shouldRegisterServiceWorker()) return;
  const { dispatch } = getStoreRef();
  captureInstallPrompt((available) => dispatch(setInstallAvailable(available)));
  controller = createController();
  controller
    .init()
    .catch((error: unknown) =>
      console.error('[pwa] no se pudo registrar el service worker', error),
    );
}

/** Activa la versión nueva del service worker; al tomar el control, la página se recarga sola. */
export function applyUpdate(): void {
  controller?.applyUpdate();
}
